<template>
  <Transition name="modal">
    <div v-if="isOpen" class="modal-overlay" @click.self="close">
      <div class="modal-content">
        <div class="modal-header">
          <h3 class="modal-title">Бронирование</h3>
          <button class="modal-close" @click="close">
            <img :src="closeIcon" alt="Close" />
          </button>
        </div>

        <div class="modal-body">
          <div class="booking-info-card">
            <h2 class="booking-date">{{ formattedDate }}</h2>
            <div class="booking-time">{{ formattedTimeRange }}</div>

            <div class="booking-details">
              <div class="detail-row">
                <img :src="locationIcon" alt="" class="detail-icon" />
                <span class="detail-text">{{ courtName }}</span>
              </div>

              <div class="detail-row">
                <img :src="flashIcon" alt="" class="detail-icon" />
                <span class="detail-text">{{ duration }} мин</span>
              </div>
            </div>
          </div>

          <div class="paid-status-card" :class="{ pending: isCashPayment }">
            <div class="paid-status-icon">
              <img :src="checkIcon" alt="" />
            </div>
            <div class="paid-status-content">
              <div class="paid-status-text">{{ statusText }}</div>
              <div v-if="shouldShowCashPaymentStatus" class="paid-status-note">
                Обращаем ваше внимание на то, что Вам необходимо закончить оплату в клубе
              </div>
            </div>
          </div>

          <div class="success-section" :style="{ backgroundImage: `url(${backgroundSuccess})` }">
            <div class="success-content">
              <div class="success-icon-wrapper">
                <img :src="rateIcon" alt="" class="success-icon" />
              </div>

              <h2 class="success-title">Спасибо!</h2>
              <p class="success-text">{{ successText }}</p>

              <a :href="cabinetLink" class="cabinet-link" @click="close">
                В личный кабинет
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
/* global defineProps, defineEmits */
import { computed } from 'vue'
import closeIcon from '@/widgets/booking/assets/img/icons/booking-close.svg'
import locationIcon from '@/widgets/booking/assets/img/icons/booking-location-blue.svg'
import flashIcon from '@/widgets/booking/assets/img/icons/flash-blue.svg'
import rateIcon from '@/widgets/booking/assets/img/icons/rate-blue.svg'
import backgroundSuccess from '@/widgets/booking/assets/img/background-success-booking.svg'
import checkIcon from '@/widgets/booking/assets/img/icons/check-white.svg'

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true
  },
  date: {
    type: String,
    default: undefined
  },
  time: {
    type: String,
    default: undefined
  },
  courtName: {
    type: String,
    default: undefined
  },
  duration: {
    type: Number,
    default: undefined
  },
  trainerName: {
    type: String,
    default: undefined
  },
  paymentMethod: {
    type: String,
    default: 'card'
  },
  paymentAmount: {
    type: Number,
    default: 0
  }
})

const emit = defineEmits(['close'])

const formattedDate = computed(() => {
  if (!props.date) return ''

  const date = new Date(props.date)
  const weekday = date.toLocaleDateString('ru-RU', { weekday: 'long' })
  const dayMonth = date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })

  const capitalizedWeekday = weekday.charAt(0).toUpperCase() + weekday.slice(1)

  return `${capitalizedWeekday}, ${dayMonth}`
})

const shortFormattedDate = computed(() => {
  if (!props.date) return ''

  const date = new Date(props.date)
  const weekday = date.toLocaleDateString('ru-RU', { weekday: 'short' }).replace('.', '')
  const dayMonth = date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })

  return `${weekday}, ${dayMonth}`
})

const isCashPayment = computed(() => props.paymentMethod === 'cash')
const shouldShowCashPaymentStatus = computed(() => isCashPayment.value && Number(props.paymentAmount || 0) > 0)

const statusText = computed(() => {
  return shouldShowCashPaymentStatus.value ? 'Оплата в клубе' : 'Бронь оплачена'
})

const successText = computed(() => {
  if (shouldShowCashPaymentStatus.value) {
    return `Бронирование на ${shortFormattedDate.value}, ${props.time} оформлено. Оплата будет произведена наличными в клубе. Запись отобразится в вашем личном кабинете в разделе «Мои записи»`
  }

  return `Вы успешно оплатили бронирование на ${shortFormattedDate.value}, ${props.time}. Запись отобразится в вашем личном кабинете в разделе «Мои записи»`
})

const formattedTimeRange = computed(() => {
  if (!props.time || !props.duration) return props.time || ''

  // Parse start time
  const [hours, minutes] = props.time.split(':').map(Number)
  const startDate = new Date()
  startDate.setHours(hours, minutes, 0, 0)

  // Calculate end time
  const endDate = new Date(startDate.getTime() + props.duration * 60000)
  const endHours = String(endDate.getHours()).padStart(2, '0')
  const endMinutes = String(endDate.getMinutes()).padStart(2, '0')

  return `${props.time} – ${endHours}:${endMinutes}`
})

const cabinetLink = computed(() => {
  if (typeof window === 'undefined') {
    return '/lk/reservations'
  }

  return `${window.location.origin}/lk/reservations`
})

const close = () => {
  emit('close')
}
</script>

<style scoped lang="scss">
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
}

.modal-content {
  position: relative;
  max-width: 500px;
  width: 100%;
  background: white;
  border-radius: 30px;
  overflow: hidden;
}

.modal-header {
  position: relative;
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-title {
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 17px;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
  color: #333333;
  margin: 0;
}

.modal-close {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #F3F5F8;
  border-radius: 7px;
  border: none;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }

  img {
    width: 14px;
    height: 14px;
  }
}

.modal-body {
  padding: 0 20px 20px;
}

.booking-info-card {
  background: #F3F5F8;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
}

.paid-status-card {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  margin-bottom: 20px;
  border-radius: 12px;
  background: #4EB410;
}

.paid-status-card.pending {
  background: #4EB410;
}

.paid-status-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.paid-status-icon {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.15);
}

.paid-status-icon img {
  width: 16px;
  height: 16px;
  display: block;
}

.paid-status-text {
  color: #FFFFFF;
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 17px;
  font-weight: 700;
  line-height: 20px;
}

.paid-status-card.pending .paid-status-text {
  color: #FFFFFF;
}

.paid-status-note {
  color: #FFFFFF;
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.paid-status-card.pending .paid-status-note {
  color: #FFFFFF;
}

.booking-date {
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 24px;
  color: #333333;
  margin: 0 0 8px 0;
  text-transform: capitalize;
  text-align: left;
}

.booking-time {
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 20px;
  text-align: left;
  color: #757885;
  margin-bottom: 16px;
}

.booking-details {
  display: flex;
  justify-content: space-between;
  gap: 24px;
}

.detail-row {
  display: flex;
  align-items: center;
  gap: 4px;
}

.detail-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.detail-text {
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  text-align: center;
  color: #757885;
}

.success-section {
  position: relative;
  padding: 20px 40px;
  text-align: center;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 12px;
}

.success-content {
  position: relative;
  z-index: 1;
}

.success-icon-wrapper {
  width: 36px;
  height: 36px;
  margin: 0 auto 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--booking-widget-primary-color-soft, #3C67D61A);
  border-radius: 12px;
  padding: 10px;
}

.success-icon {
  width: 20px;
  height: 20px;
  display: block;
}

.success-title {
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 17px;
  font-weight: 700;
  line-height: 20px;
  color: #333333;
  margin: 0 0 12px 0;
}

.success-text {
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  text-align: center;
  color: #757885;
  margin: 0 0 16px 0;
}

.cabinet-link {
  display: inline-block;
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
  color: var(--booking-widget-primary-color, #3C67D6);
  text-decoration: none;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
}

// Transition animations
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .modal-content,
.modal-leave-active .modal-content {
  transition: transform 0.3s ease;
}

.modal-enter-from .modal-content,
.modal-leave-to .modal-content {
  transform: scale(0.9);
}
</style>
